import React, { useCallback, useEffect, useState } from 'react';
import { Container, Box, Grid, Radio, RadioGroup, FormControlLabel, Button, FormLabel } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Typography, ComboSelect } from '../../components/atoms';
import { ErrorSpan } from '../../containers/retirement-goals-drawer';
import { useAppDispatch, useAppSelector } from '../../common/hooks';
import { useDropzone, FileRejection } from 'react-dropzone';
import { uploadPortfolioFile } from '../../features/adviceAdmin';
import { updateGlobalLoaderState } from '../../features/global/globalSlice';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import excel from '../../assets/icons/excel.svg';
import uploadIcon from '../../assets/icons/upload.svg';
import PortfolioApprovals from '../portfolio-approvals';
import {
    FEATURE_NAMES,
    fundFilledOptions,
    goalCategoryOptions,
    PropertyOptions,
    WeightOptions,
    WeightTypeOptions
} from '../../common/constants';
import { handleSamplePortfolioDownload } from '../portfolio-approvals/hooks';
import { checkFFEnabled } from '../../utils';
import { Toast } from '../../components/molecules/Toast/Toast';

const StyledExcelImg = styled('img')({
    width: '24px',
    height: '24px'
});

const StyledSelect = styled(ComboSelect)(() => ({
    '&.Mui-disabled': {
        cursor: 'not-allowed'
    }
}));

const CustomTypography = styled(Typography)<{ custom_color?: string }>(({ custom_color }) => ({
    color: custom_color || '#000'
}));

const StyledRadioGroup = styled(RadioGroup)(() => ({
    gap: '10px',
    '& .MuiFormControlLabel-root': {
        marginBottom: '8px'
    },
    '& .MuiFormControlLabel-label': {
        fontSize: '16px'
    }
}));

const PropertyTypography = styled(Typography)(() => ({
    display: 'flex',
    flexDirection: 'row',
    gap: 4
}));

const StyledWTypeBox = styled(Box)(() => ({
    backgroundColor: 'lightgray',
    width: 'max-content',
    borderRadius: '10px'
}));

const StyledUploadImg = styled('img')({
    width: '24px',
    height: '24px',
    color: '#474746ff'
});

const StyledBox = styled(Box)(() => ({
    border: '1px solid #E6E6E6',
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.05)',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: 'background.paper',
    width: '100%',
    p: 2,
    margin: '0 0 20px 0'
}));

const StyledInnerGrid = styled(Grid)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '5px'
}));

const StyledFlexEndGrid = styled(Grid)(() => ({
    display: 'flex',
    justifyContent: 'flex-end'
}));

const StyledWTBox = styled(Box)(() => ({
    cursor: 'pointer',
    display: 'inline-block',
    '& input': {
        display: 'none'
    }
}));

const StyledFlexRowTypography = styled(Typography)(() => ({
    display: 'flex',
    flexDirection: 'row',
    gap: 4
}));

const StyledUploadGrid = styled(Grid)(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    width: '100%'
}));

const StyledUploadInnerGrid = styled(Grid)(() => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
}));

const StyleDropZoneGrid = styled(Grid)(() => ({
    border: '2px dashed #ccc',
    padding: 2,
    cursor: 'pointer',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'center',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    minHeight: '100px',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column'
}));

const StyleUploadErrorTypography = styled(Typography)<{ color_custom?: string }>(({ color_custom }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    position: 'relative',
    zIndex: 2,
    background: '#fff',
    padding: '4px 2px',
    borderRadius: '4px',
    height: '5px',
    color: color_custom || 'black'
}));

const StyledDownloadButton = styled(Button)(() => ({
    textTransform: 'none',
    padding: '5px 10px',
    border: 'none'
}));

const StyledClearButton = styled(Button)<{ error?: boolean }>(({ error }) => ({
    display: error ? 'inline-block' : 'none',
    textTransform: 'none',
    position: 'relative',
    height: '20px',
    marginTop: '5px',
    cursor: 'pointer',
    fontSize: '12px',
    backgroundColor: '#f0f0f0',
    color: '#000',
    border: '1px solid #ccc',
    borderRadius: '5px',
    padding: '0px 8px',
    '&:hover': {
        backgroundColor: '#e0e0e0'
    }
}));

const StyledResetButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    padding: '20px 30px',
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.primary[500],
    color: theme.palette.neutral.white,
    borderRadius: '20px',
    cursor: 'pointer',
    marginRight: '10px'
}));

const StyledSubmitButton = styled(Button)<{ isValid?: boolean }>(({ theme, isValid }) => ({
    backgroundColor: theme.palette.primary[500],
    cursor: !isValid ? 'not-allowed' : 'pointer',
    textTransform: 'none',
    padding: '20px 30px',
    whiteSpace: 'nowrap',
    color: theme.palette.neutral.white,
    variant: 'contained',
    borderRadius: '20px',
    ':disabled': {
        backgroundColor: theme.palette.neutral[100],
        color: theme.palette.neutral[300],
        cursor: 'not-allowed'
    }
}));

const StyledUploadErrorUl = styled('ul')(() => ({
    margin: 0,
    paddingLeft: 16
}));

const StyledFormLabelTypography = styled(Typography)(() => ({
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: '4px'
}));

const StyledToastBox = styled(Box)(({}) => ({
    width: '100%',
    backgroundColor: '#fbf9fa',
    color: '#424242ff',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: '1px solid #0000001a',
    boxShadow: '0.2',
    padding: '16px 16px'
}));

const STyledToastGrid = styled(Grid)(() => ({
    cursor: 'pointer',
    float: 'right',
    borderRadius: '20%',
    '&:hover': {
        backgroundColor: '#939393d7'
    }
}));

const StyledLabelTypography = styled(FormLabel)<{ isValue?: boolean }>(({ isValue = false }) => ({
    display: 'block',
    width: '100%',
    height: '100%',
    padding: '8px 16px',
    borderRadius: 5,
    cursor: 'pointer',
    boxSizing: 'border-box',
    userSelect: 'none',
    border: isValue ? '2px solid #1976d2' : '1px solid #ccc',
    background: isValue ? '#1976d2' : 'lightgray',
    color: isValue ? '#fff' : '#000'
}));

export interface FileUploadComponentProps {
    // eslint-disable-next-line no-unused-vars
    onChange: (files: File[]) => void;
    resetFile: boolean;
    // eslint-disable-next-line no-unused-vars
    setResetFile?: (value: boolean) => void;
}

function FileUploadComponent({ onChange, resetFile, setResetFile }: FileUploadComponentProps) {
    const [files, setFiles] = useState<File[]>([]);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const { t } = useTranslation();

    useEffect(() => {
        if (resetFile) {
            setFiles([]);
            onChange([]);
            setErrorMessages([]);
            if (setResetFile) setResetFile(false);
        }
    }, [resetFile]);

    const onDropRejected = (fileRejections: FileRejection[]) => {
        const errors = fileRejections.map(({ file, errors }) => {
            return `${file.name}: ${errors.map((e: any) => e.message).join(', ')}`;
        });
        setErrorMessages(errors);
    };

    const onDrop = useCallback((acceptedFiles: any) => {
        onChange(acceptedFiles);
        setErrorMessages([]);
        setFiles((prev) => [...prev, ...acceptedFiles]);
    }, []);

    const removeFile = (fileName: any) => {
        onChange([]);
        setFiles((prevFiles) => prevFiles.filter((file) => file.name !== fileName));
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
        },
        multiple: false,
        // maxSize: 2 * 1024 * 1024, // Optional: 2MB limit
        onDropRejected
    });

    return (
        <StyledUploadGrid>
            {files.length === 0 && (
                <StyleDropZoneGrid {...getRootProps()}>
                    <input {...getInputProps()} />
                    <StyledUploadImg src={uploadIcon} alt="Upload Icon" />
                    {isDragActive ? (
                        t('DROP_THE_FLIES_HERE_LABEL')
                    ) : (
                        <>
                            <Grid display={'flex'} flexDirection="row" alignItems="center">
                                <CustomTypography variant="body1" custom_color="#1976d2">
                                    {' '}
                                    {t('CLICK_TO_SELECT')}
                                </CustomTypography>
                                <Typography variant="body2">&nbsp;&nbsp;{t('DRAG_AND_DROP_TEXT')}</Typography>
                            </Grid>
                            <CustomTypography variant="body2" custom_color="#424242">
                                {t('EXCEL_FILES_ONLY_TEXT')}
                            </CustomTypography>
                        </>
                    )}
                </StyleDropZoneGrid>
            )}

            {files.length > 0 && (
                <Box mt={2} width="100%">
                    <Grid container alignItems="center" mb={1}>
                        <Grid item xs={12}>
                            <StyledToastBox>
                                <Grid item display="flex" alignItems="center" width="100%" gap={2}>
                                    <StyledExcelImg color="green" src={excel} alt="Excel Icon" />
                                    <CustomTypography variant="body1" custom_color="#2e2e2eff">
                                        {files[0].name} — {(files[0].size / 1024).toFixed(2)} KB
                                    </CustomTypography>
                                </Grid>
                                <STyledToastGrid item width="24px" height="24px">
                                    <CloseIcon color="inherit" onClick={() => removeFile(files[0].name)} />
                                </STyledToastGrid>
                            </StyledToastBox>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {errorMessages.length > 0 && (
                <StyleUploadErrorTypography variant="body2" mt={2} color_custom={'red'}>
                    <strong>{t('FILE_UPLOAD_ERROR_TEXT')}</strong>
                    <StyledUploadErrorUl>
                        {errorMessages.map((msg, index) => (
                            <li key={index}>{msg}</li>
                        ))}
                    </StyledUploadErrorUl>
                </StyleUploadErrorTypography>
            )}
        </StyledUploadGrid>
    );
}

export interface PortfolioManagementProps {}

type ConfigType = {
    property: string;
    tenant: string;
    weightType: string;
    selectedWeight: string;
    fundFilled: string;
    portfolioFile: File | null;
    portfolioBundle?: string;
    goalType?: string;
};

const PortfolioManagement: React.FC<PortfolioManagementProps> = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [deleteFile, setDeletFile] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errorType, setErrorType] = useState<string | null>(null);
    const [successToast, setSuccessToast] = useState<{ open: boolean; message: string }>({
        open: false,
        message: ''
    });
    const banorte_flow2_ff = checkFFEnabled(FEATURE_NAMES['DA_FLOW2_FE_AEX-2751_AEX-2626']);
    const goalCategories =
        useAppSelector((state) => state?.global?.orgInfo?.goalCategories?.en) || goalCategoryOptions(t);
    const portfolioLevelConfig = useAppSelector((state) => state?.global?.goeConfig?.portfolioLevelConfig) || [];
    const bundleOptions: Array<{ label: string; value: string }> =
        portfolioLevelConfig?.map((config: any) => ({ label: config?.labelName, value: config?.labelName })) || [];
    const tenantOptions: Array<{ value: string; label: string }> = useAppSelector(
        (state) => state?.auth?.user?.admin_tenantList
    );

    const getWeightTypeOptionsComponents = (field: any) => {
        return WeightTypeOptions.map(({ label, value }) => (
            <StyledWTBox key={value} paddingTop={'0px'} height={40}>
                <input
                    type="radio"
                    id={value}
                    value={value}
                    checked={field.value === value}
                    onChange={() => field.onChange(value)}
                />

                <StyledLabelTypography
                    htmlFor={value}
                    onClick={() => field.onChange(value)}
                    isValue={field.value === value}
                >
                    {t(label)}
                </StyledLabelTypography>
            </StyledWTBox>
        ));
    };

    const initialFormValues: ConfigType = {
        property: t('DIGITAL_ADVISOR'),
        tenant: '',
        weightType: 'strategic_only',
        selectedWeight: '',
        fundFilled: '',
        portfolioFile: null,
        portfolioBundle: '',
        goalType: ''
    };

    const { control, formState, handleSubmit, reset, setValue, watch } = useForm<ConfigType>({
        mode: 'all',
        reValidateMode: 'onSubmit',
        defaultValues: initialFormValues
    });
    const { errors, isValid } = formState;

    const onSubmit = async (data: ConfigType) => {
        await dispatch(updateGlobalLoaderState(true));

        let response = await dispatch(
            uploadPortfolioFile({
                file: data.portfolioFile,
                tenant: data.tenant,
                tenant_supported_weight_type: data.weightType,
                weight_type: data.weightType == 'both' ? data.selectedWeight : 'strategic',
                is_ft_fullfilled: data.fundFilled === 'Yes' ? true : false,
                portfolioBundleName: data.portfolioBundle,
                goalCategory: data.goalType
            })
        );
        console.log('response', response);
        if (response?.payload?.statusCode === 400) {
            setError(response?.payload?.message || t('FILE_UPLOAD_ERROR'));
            setErrorType(response?.payload?.name || '');
        }

        if (response?.payload?.success) {
            setError(null);
            setErrorType(null);
            setSuccessToast({
                open: true,
                message: t('PORTFOLIO_UPLOAD_SUCCESS')
            });
            console.log('File uploaded successfully:', response.payload);

            reset({ ...initialFormValues, tenant: data.tenant });
            setValue('portfolioFile', null);
            setDeletFile(true);
        }

        await dispatch(updateGlobalLoaderState(false));
    };

    return (
        <form id="hook-form" onSubmit={handleSubmit(onSubmit)}>
            <Container>
                <StyledBox id="portfolio-management-form">
                    <PortfolioApprovals />
                </StyledBox>

                <StyledBox p={3}>
                    <Grid
                        width="100%"
                        item
                        flexDirection="row"
                        container
                        spacing={8}
                        justifyContent="left"
                        alignItems="center"
                    >
                        <StyledInnerGrid item xs={12} sm={6} md={5}>
                            <StyledFormLabelTypography>{t('SELECT_PROPERTY')}</StyledFormLabelTypography>
                            <Controller
                                name="property"
                                control={control}
                                rules={{ required: t('REQUIRED_FILED') }}
                                render={({ field }) => (
                                    <StyledSelect
                                        onSelectChange={field.onChange}
                                        selectedValue={field.value}
                                        size="small"
                                        fullWidth
                                        data-testid="property-select"
                                        error={!!errors.property}
                                        placeholder={t('CHOOSE_A_PROPERTY')}
                                        options={PropertyOptions.map(({ value, label }) => ({
                                            value,
                                            label: t(label)
                                        }))}
                                    />
                                )}
                            />
                            <PropertyTypography variant="subtitle2">
                                <Box component="span" mt={'5px'}>
                                    {t('PROPERTY_DISCLAIMER')}
                                </Box>
                                {errors.property && (
                                    <ErrorSpan variant="subtitle2" role="alert" label={errors.property.message} />
                                )}
                            </PropertyTypography>
                        </StyledInnerGrid>

                        <StyledInnerGrid item xs={12} sm={6} md={5}>
                            <StyledFormLabelTypography>{t('SELECT_TENANT')}</StyledFormLabelTypography>
                            <Controller
                                name="tenant"
                                control={control}
                                rules={{ required: t('REQUIRED_FILED') }}
                                render={({ field }) => (
                                    <StyledSelect
                                        onSelectChange={field.onChange}
                                        selectedValue={field.value}
                                        size="small"
                                        fullWidth
                                        data-testid="tenant-select"
                                        error={!!errors.tenant}
                                        placeholder={t('CHOOSE_A_TENANT')}
                                        options={tenantOptions.map(({ value, label }) => ({ value, label: t(label) }))}
                                    />
                                )}
                            />
                            <PropertyTypography variant="subtitle2">
                                <Box component="span" mt={'5px'}>
                                    {t('PORTFOLIO_TENANT_DISCLAIMER')}
                                </Box>
                                {errors.tenant && (
                                    <ErrorSpan ml={2} variant="subtitle2" role="alert" label={errors.tenant.message} />
                                )}
                            </PropertyTypography>
                        </StyledInnerGrid>
                    </Grid>

                    {banorte_flow2_ff && watch('tenant') === 'banortemex' && (
                        <Grid
                            width="100%"
                            item
                            flexDirection="row"
                            container
                            spacing={8}
                            justifyContent="left"
                            alignItems="center"
                        >
                            <StyledInnerGrid item xs={12} sm={6} md={5}>
                                <StyledFormLabelTypography>{t('GOAL_TYPE')}</StyledFormLabelTypography>
                                <Controller
                                    name="goalType"
                                    control={control}
                                    rules={{ required: t('REQUIRED_FILED') }}
                                    render={({ field }) => (
                                        <StyledSelect
                                            onSelectChange={field.onChange}
                                            selectedValue={field.value || ''}
                                            size="small"
                                            fullWidth
                                            data-testid="goalType-select"
                                            error={!!errors.goalType}
                                            placeholder={t('SELECT_GOAL_TYPE_PLACEHOLDER')}
                                            options={
                                                Array.isArray(goalCategories)
                                                    ? goalCategories.map(({ value, label }) => ({
                                                          value,
                                                          label: t(label)
                                                      }))
                                                    : (goalCategories as any)['en']?.map(({ value, label }: any) => ({
                                                          value,
                                                          label: t(label)
                                                      })) || []
                                            }
                                        />
                                    )}
                                />
                                <PropertyTypography variant="subtitle2">
                                    <Box component="span" mt={'5px'}>
                                        {t('GOAL_TYPE_DISCLAIMER')}
                                    </Box>
                                    {errors.goalType && (
                                        <ErrorSpan
                                            ml={2}
                                            variant="subtitle2"
                                            role="alert"
                                            label={errors.goalType.message}
                                        />
                                    )}
                                </PropertyTypography>
                            </StyledInnerGrid>

                            <StyledInnerGrid item xs={12} sm={6} md={5}>
                                <StyledFormLabelTypography>{t('PORTFOLIO_BUNDLE')}</StyledFormLabelTypography>
                                <Controller
                                    name="portfolioBundle"
                                    control={control}
                                    rules={{ required: t('REQUIRED_FILED') }}
                                    render={({ field }) => (
                                        <StyledSelect
                                            onSelectChange={field.onChange}
                                            selectedValue={field.value || ''}
                                            size="small"
                                            fullWidth
                                            data-testid="bundle-select"
                                            error={!!errors.portfolioBundle}
                                            placeholder={t('SELECT_BUNDLE_PLACEHOLDER')}
                                            options={bundleOptions.map(({ value, label }) => ({
                                                value,
                                                label: t(label)
                                            }))}
                                        />
                                    )}
                                />
                                <PropertyTypography variant="subtitle2">
                                    <Box component="span" mt={'5px'}>
                                        {t('BUNDLE_DISCLAIMER')}
                                    </Box>
                                    {errors.portfolioBundle && (
                                        <ErrorSpan
                                            variant="subtitle2"
                                            role="alert"
                                            label={errors.portfolioBundle.message}
                                        />
                                    )}
                                </PropertyTypography>
                            </StyledInnerGrid>
                        </Grid>
                    )}

                    <Grid
                        width="100%"
                        item
                        flexDirection="row"
                        container
                        spacing={8}
                        justifyContent="left"
                        alignItems="center"
                    >
                        <StyledInnerGrid item xs={12} sm={6} md={4} gap={'10px'}>
                            <StyledFormLabelTypography>{t('SELECT_WEIGHT_TYPE')}</StyledFormLabelTypography>
                            <Controller
                                name="weightType"
                                control={control}
                                rules={{ required: { value: false, message: t('REQUIRED_FILED') } }}
                                render={({ field }) => (
                                    <StyledWTypeBox display="flex" flexDirection="row" gap={1} height={40}>
                                        {getWeightTypeOptionsComponents(field)}
                                    </StyledWTypeBox>
                                )}
                            />
                            <StyledFlexRowTypography variant="subtitle2">
                                {errors.weightType && (
                                    <ErrorSpan
                                        ml={2}
                                        variant="subtitle2"
                                        role="alert"
                                        label={errors.weightType.message}
                                    />
                                )}
                            </StyledFlexRowTypography>
                        </StyledInnerGrid>
                    </Grid>

                    <Grid
                        width="100%"
                        item
                        flexDirection="row"
                        container
                        pl={4}
                        justifyContent="left"
                        alignItems="center"
                    >
                        <StyledInnerGrid item gap={'10px'}>
                            <StyledFormLabelTypography>{t('WEIGHT_TYPE_LABEL')}</StyledFormLabelTypography>
                            <Controller
                                name="selectedWeight"
                                control={control}
                                rules={{
                                    required: {
                                        value: watch('weightType') === 'strategic_only' ? false : true,
                                        message: t('REQUIRED_FILED')
                                    }
                                }}
                                render={({ field }) => (
                                    <StyledRadioGroup
                                        {...field}
                                        row
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        {WeightOptions.map(({ label, value }) => (
                                            <FormControlLabel
                                                key={value}
                                                value={value}
                                                control={<Radio />}
                                                label={t(label)}
                                                disabled={watch('weightType') === 'strategic_only'}
                                            />
                                        ))}
                                    </StyledRadioGroup>
                                )}
                            />
                            {errors.selectedWeight && (
                                <ErrorSpan
                                    ml={2}
                                    variant="subtitle2"
                                    role="alert"
                                    label={errors.selectedWeight.message}
                                />
                            )}
                        </StyledInnerGrid>
                    </Grid>

                    <Grid
                        width="100%"
                        item
                        flexDirection="row"
                        container
                        spacing={8}
                        justifyContent="left"
                        alignItems="center"
                    >
                        <StyledInnerGrid item gap={'10px'}>
                            <StyledFormLabelTypography>{t('FUNDFILLED_LABEL')}</StyledFormLabelTypography>
                            <Controller
                                name="fundFilled"
                                control={control}
                                rules={{ required: t('REQUIRED_FILED') }}
                                render={({ field }) => (
                                    <StyledRadioGroup
                                        {...field}
                                        row
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        {fundFilledOptions.map(({ label, value }) => (
                                            <FormControlLabel
                                                key={value}
                                                value={value}
                                                control={<Radio />}
                                                label={t(label)}
                                            />
                                        ))}
                                    </StyledRadioGroup>
                                )}
                            />
                            {errors.fundFilled && (
                                <ErrorSpan ml={2} variant="subtitle2" role="alert" label={errors.fundFilled.message} />
                            )}
                        </StyledInnerGrid>
                    </Grid>

                    <Grid
                        width="100%"
                        item
                        flexDirection="column"
                        container
                        spacing={8}
                        justifyContent="left"
                        alignItems="center"
                    >
                        {/* upload document section */}
                        <StyledUploadGrid item xs={12}>
                            <StyledUploadInnerGrid
                                width="100%"
                                item
                                flexDirection="row"
                                container
                                justifyContent="left"
                            >
                                <StyledFormLabelTypography>{t('UPLOAD_LABEL')}</StyledFormLabelTypography>

                                <StyledDownloadButton onClick={() => handleSamplePortfolioDownload()}>
                                    {t('DOWNLOAD_TEMPLATE')}
                                </StyledDownloadButton>
                            </StyledUploadInnerGrid>

                            <Controller
                                name="portfolioFile"
                                control={control}
                                rules={{ required: t('UPLOAD_VALIDATION_ERROR') }}
                                render={({ field }) => (
                                    <FileUploadComponent
                                        onChange={(files) => {
                                            field.onChange(files[0]);
                                            setValue('portfolioFile', files[0]);
                                            if (error || errorType) {
                                                setError(null);
                                                setErrorType(null);
                                            }
                                        }}
                                        resetFile={deleteFile}
                                        setResetFile={setDeletFile}
                                    />
                                )}
                            />

                            <StyledFlexRowTypography variant="subtitle2">
                                <Box component="span" mt={'5px'}>
                                    {t('UPLOAD_DISCLAIMER')}
                                </Box>
                            </StyledFlexRowTypography>

                            <StyleUploadErrorTypography variant="subtitle2">
                                <Box component="span" mt={'5px'} color={'red'}>
                                    {errorType} {error ? ':' : null} {error}
                                </Box>
                                <StyledClearButton
                                    type="button"
                                    error={!!error}
                                    onClick={() => {
                                        setError(null);
                                        setErrorType(null);
                                    }}
                                >
                                    {t('CLEAR')}
                                </StyledClearButton>
                            </StyleUploadErrorTypography>
                        </StyledUploadGrid>
                    </Grid>

                    <Grid
                        width="100%"
                        item
                        flexDirection="row"
                        container
                        spacing={8}
                        justifyContent="flex-end"
                        alignItems="center"
                    >
                        <StyledFlexEndGrid item xs={12} sm={6} md={4} sx={{ pl: '0 !important' }}>
                            {/* Reset button */}
                            <StyledResetButton
                                variant="contained"
                                type="button"
                                onClick={() => {
                                    reset(initialFormValues);
                                    setDeletFile(true);
                                    setValue('portfolioFile', null);
                                    setError(null);
                                    setErrorType(null);
                                }}
                            >
                                {/* override rest to clear the file upload area */}
                                {t('RESET_FORM')}
                            </StyledResetButton>

                            <StyledSubmitButton variant="contained" type="submit" disabled={!isValid} isValid={isValid}>
                                {t('SUBMIT')}
                            </StyledSubmitButton>
                        </StyledFlexEndGrid>
                    </Grid>
                </StyledBox>
            </Container>
            {successToast.open && (
                <Toast
                    heading={successToast.message}
                    type="confirm"
                    open={successToast.open}
                    handleClose={() => setSuccessToast({ open: false, message: '' })}
                    isTimer
                    disappearTime={3}
                />
            )}
        </form>
    );
};

export default PortfolioManagement;
